import { CredentialsSignin as CredentialsSigninError } from "@auth/core/errors";
import { NEXTAUTH_SECRET } from "@pharaday/env";
import { injectable } from "inversify";
import NextAuth, { type NextAuthConfig, type NextAuthResult } from "next-auth";
import type { NextAuthRequest } from "next-auth/lib";
import CredentialsProvider from "next-auth/providers/credentials";
import type { ReadonlyRequestCookies } from "next/dist/server/web/spec-extension/adapters/request-cookies";
import { NextResponse } from "next/server";
import { pathToRegexp } from "path-to-regexp";

import {
  AuthenticatorProvider,
  type AuthMiddlewareConfiguration,
  type AuthProvider,
  type Credentials,
} from "../../application";
import { AuthContainer } from "../../auth.container";
import { AccessToken, type Session, SessionErrorEnum } from "../../domain";

export const COOKIE_SIGN_IN_CALLBACK_URL = "signInCallbackUrl";

class AuthEmailVerificationRequiredError extends CredentialsSigninError {
  constructor(pendingToken: string) {
    super();
    this.code = JSON.stringify({
      reason: "AUTH_EMAIL_VERIFICATION_REQUIRED",
      pendingToken,
    });
  }
}

@injectable()
export class NextAuthProvider implements AuthProvider {
  private nextAuth: NextAuthResult;

  constructor() {
    this.nextAuth = NextAuth(this.getNextAuthConfig());
  }

  private getNextAuthConfig(): NextAuthConfig {
    return {
      secret: AuthContainer.get<string>(NEXTAUTH_SECRET),
      providers: [
        CredentialsProvider({
          authorize: async params => {
            if (!params) {
              return null;
            }

            const credentials = params as Credentials;

            try {
              switch (credentials.type) {
                case "with-password": {
                  const result = await AuthContainer.get(AuthenticatorProvider).signInWithPassword(
                    credentials.email,
                    credentials.password
                  );

                  if ("pendingToken" in result) {
                    throw new AuthEmailVerificationRequiredError(result.pendingToken);
                  }

                  return result;
                }

                case "with-magic-link": {
                  return await AuthContainer.get(AuthenticatorProvider).signInWithMagicLink(
                    credentials.email,
                    credentials.code
                  );
                }

                case "with-totp": {
                  return await AuthContainer.get(AuthenticatorProvider).signInWithTOTP(
                    credentials.pendingToken,
                    credentials.challenge,
                    credentials.code
                  );
                }

                case "with-email-validation": {
                  return await AuthContainer.get(AuthenticatorProvider).signInWithValidationEmail(
                    credentials.pendingToken,
                    credentials.code
                  );
                }
              }
            } catch (error) {
              if (error instanceof AuthEmailVerificationRequiredError) {
                throw error;
              }

              return null;
            }
          },
        }),
      ],
      session: { strategy: "jwt" },
      callbacks: {
        jwt: async ({ token, user }) => {
          if (user) {
            return { ...token, ...user };
          }

          if (Date.now() < token.accessToken.expiresAt * 1000) {
            return token;
          }

          try {
            const credential = await AuthContainer.get(AuthenticatorProvider).requestRefreshToken(
              token.accessToken,
              token.refreshToken
            );

            return {
              accessToken: credential.accessToken,
              refreshToken: credential.refreshToken,
            };
            // eslint-disable-next-line sonarjs/no-ignored-exceptions
          } catch (_error) {
            return { ...token, error: SessionErrorEnum.REFRESH_ACCESS_TOKEN_ERROR as const };
          }
        },

        async session(params) {
          const { session } = params;

          if ("token" in params) {
            session.accessToken = params.token.accessToken;
            session.error = params.token.error;
            return session;
          }

          // TODO: If use UserAdapter
          // if ("user" in params) {
          //    ...
          // }

          return session;
        },
      },
      events: {
        signOut: async params => {
          if ("token" in params && params.token) {
            await AuthContainer.get(AuthenticatorProvider).signOut(params.token.accessToken, params.token.refreshToken);
          }
        },
      },
    };
  }

  getRouteHandlers() {
    const { GET, POST } = this.nextAuth.handlers;
    return { GET, POST };
  }

  toSession(nextAuthSession: Session) {
    return {
      accessToken: new AccessToken(nextAuthSession.accessToken.value),
    };
  }

  authMiddleware(configuration: AuthMiddlewareConfiguration) {
    const API_AUTH_PREFIX = "/api/auth";

    const isUrl = (route: string) => route.startsWith("http");

    const handleGuestRoute = (req: NextAuthRequest) => {
      const isSignedIn = req.auth && !req.auth.error;

      if (isSignedIn) {
        const signInCallbackURL = configuration.signInCallbackURL
          ? // eslint-disable-next-line sonarjs/no-nested-conditional
            new URL(configuration.signInCallbackURL, !isUrl(configuration.signInCallbackURL) ? req.nextUrl : undefined)
          : new URL("/", req.nextUrl);

        return NextResponse.redirect(signInCallbackURL);
      }

      return configuration.afterAuth ? configuration.afterAuth(req) : undefined;
    };

    return this.nextAuth.auth(async (req: NextAuthRequest) => {
      const { nextUrl } = req;
      const pathname = nextUrl.pathname;

      const routeMatchPathname = (route: string) => pathToRegexp(route).regexp.test(pathname);

      const isSignedIn = req.auth && !req.auth.error;
      const isApiAuthRoute = pathname.startsWith(API_AUTH_PREFIX);
      const isGuestRoute = configuration.guestRoutes?.some(routeMatchPathname);

      const isPublicRoute = configuration.publicRoutes?.some(routeMatchPathname);

      if (isApiAuthRoute) {
        return undefined;
      }

      if (isGuestRoute) {
        return handleGuestRoute(req);
      }

      if (!isSignedIn && !isPublicRoute) {
        const error = req.auth?.error;
        const signInURL = new URL(
          error ? `${configuration.signInURL}?error=${error}` : configuration.signInURL,
          !isUrl(configuration.signInURL) ? nextUrl : undefined
        );
        const response = NextResponse.redirect(signInURL);
        response.cookies.set(COOKIE_SIGN_IN_CALLBACK_URL, nextUrl.toString(), {
          httpOnly: true,
          secure: true,
          sameSite: "lax",
          path: "/",
        });
        return response;
      }

      const response = configuration.afterAuth
        ? // eslint-disable-next-line sonarjs/no-nested-conditional
          await configuration.afterAuth(req, req.auth ? this.toSession(req.auth) : undefined)
        : NextResponse.next();

      if (isSignedIn && req.cookies?.get(COOKIE_SIGN_IN_CALLBACK_URL)) {
        response.cookies.delete(COOKIE_SIGN_IN_CALLBACK_URL);
        return response;
      }

      return response;
    });
  }

  getSignInCallbackUrl(cookie: ReadonlyRequestCookies) {
    return cookie.get(COOKIE_SIGN_IN_CALLBACK_URL)?.value;
  }

  async signIn(_credentials: Credentials): Promise<{ success: boolean }> {
    // Defensive programming, this should not ever be happened
    throw new Error("Method not implemented.");
  }

  async signOut(): Promise<{ success: boolean }> {
    // Defensive programming, this should not ever be happened
    throw new Error("Method not implemented.");
  }

  async getSession(): Promise<Session | null> {
    return this.nextAuth.auth();
  }

  async updateSession(session: Session): Promise<void> {
    await this.nextAuth.unstable_update(session);
  }

  async renewSession(): Promise<Session | null> {
    const nextAuthSession = await this.nextAuth.auth();
    if (!nextAuthSession) {
      return null;
    }

    const session = this.toSession(nextAuthSession);
    return !session.accessToken.isExpired() ? session : null;
  }
}
